import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Search, ChevronDown, ChevronRight, MapPin, Church, Filter } from 'lucide-react';
import { fetchParishes } from '@/services/api';
import { useToast } from '@/hooks/use-toast';
import { Link } from 'react-router-dom';
import ImageHelper from '../lib/imageHelper';

interface Parish {
  id: number;
  name: string;
  location: string;
  archdeaconry: string;
  slug: string;
  photo?: string;
}

const Parishes = () => {
  const [parishes, setParishes] = useState<Parish[]>([]);
  const [filteredParishes, setFilteredParishes] = useState<Parish[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterArchdeaconry, setFilterArchdeaconry] = useState('all');
  const [expandedArchdeaconries, setExpandedArchdeaconries] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    const loadParishes = async () => {
      try {
        const response = await fetchParishes();
        const transformed = response.data.parishes.map((item: any) => ({
          id: item.id,
          name: item.name,
          location: item.location ?? 'Ibadan, Nigeria',
          archdeaconry: item.archdeaconry?.name ?? 'Diocesan',
          slug: item.slug,
          photo: item?.photo
        }));
        setParishes(transformed);
        setFilteredParishes(transformed);
      } catch (error) {
        toast({ title: 'Connection Error', description: 'Could not reach the parish directory.', variant: 'destructive' });
      } finally {
        setLoading(false);
      }
    };
    loadParishes();
  }, [toast]);

  useEffect(() => {
    const filtered = parishes.filter(parish => {
      const matchesSearch = parish.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        parish.location.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesArch = filterArchdeaconry === 'all' || parish.archdeaconry === filterArchdeaconry;
      return matchesSearch && matchesArch;
    });
    setFilteredParishes(filtered);
  }, [parishes, searchTerm, filterArchdeaconry]);

  const archdeaconries = [...new Set(parishes.map(p => p.archdeaconry))];

  useEffect(() => {
    const updated: Record<string, boolean> = {};
    archdeaconries.forEach(name => { updated[name] = true; });
    setExpandedArchdeaconries(updated);
  }, [parishes]);

  const groupedParishes = filteredParishes.reduce((acc, parish) => {
    if (!acc[parish.archdeaconry]) acc[parish.archdeaconry] = [];
    acc[parish.archdeaconry].push(parish);
    return acc;
  }, {} as Record<string, Parish[]>);

  const toggleExpand = (archdeaconry: string) => {
    setExpandedArchdeaconries(prev => ({ ...prev, [archdeaconry]: !prev[archdeaconry] }));
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-[#fcfaf7]">
      <div className="flex flex-col items-center">
        <div className="h-10 w-10 border-4 border-amber-200 border-t-amber-600 rounded-full animate-spin mb-4"></div>
        <p className="font-serif italic text-slate-600">Locating our churches...</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#fcfaf7] py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Page Header */}
        <div className="text-center mb-16">
          <h2 className="text-amber-600 font-bold tracking-[0.25em] uppercase text-xs mb-3">The Diocese</h2>
          <h1 className="text-4xl lg:text-6xl font-serif font-bold text-slate-900 mb-6">Parish Directory</h1>
          <div className="h-1 w-20 bg-amber-600 mx-auto mb-8"></div>

          {/* Elegant Control Bar */}
          <div className="max-w-3xl mx-auto bg-white p-2 rounded-2xl shadow-sm ring-1 ring-slate-200 flex flex-col sm:flex-row items-center gap-2">
            <div className="relative flex-1 w-full">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Find a parish or location..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="border-none focus-visible:ring-0 pl-11 h-12 bg-transparent"
              />
            </div>
            <div className="hidden sm:block h-6 w-[1px] bg-slate-200"></div>
            <div className="flex items-center w-full sm:w-auto pr-2">
              <Filter className="h-4 w-4 text-slate-400 ml-3 mr-1" />
              <Select onValueChange={setFilterArchdeaconry} value={filterArchdeaconry}>
                <SelectTrigger className="border-none focus:ring-0 bg-transparent font-medium text-slate-700 w-full sm:w-48">
                  <SelectValue placeholder="All Areas" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Archdeaconries</SelectItem>
                  {archdeaconries.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Grouped Content */}
        {Object.entries(groupedParishes).map(([arch, archParishes]) => {
          const isExpanded = expandedArchdeaconries[arch];
          return (
            <div key={arch} className="mb-12">
              <button
                onClick={() => toggleExpand(arch)}
                className="w-full flex items-center justify-between group py-4 border-b border-slate-200 mb-6"
              >
                <div className="flex items-baseline gap-4">
                  <h2 className="text-2xl font-serif font-bold text-slate-800 group-hover:text-amber-700 transition-colors">
                    {arch} <span className="text-slate-300 font-sans font-light ml-2 text-xl">/ {archParishes.length} Parishes</span>
                  </h2>
                </div>
                <div className={`transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`}>
                  <ChevronDown className="h-6 w-6 text-slate-400" />
                </div>
              </button>

              {isExpanded && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
                  {archParishes.map(parish => (
                    <Card key={parish.id} className="group border-none shadow-none bg-transparent overflow-hidden">
                      <Link to={`/parishes/${parish.slug}`} className="block">
                        <div className="relative aspect-[4/3] rounded-2xl overflow-hidden mb-4 ring-1 ring-slate-200 shadow-sm">
                          <ImageHelper
                            src={parish.photo}
                            alt={parish.name}
                            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-6">
                            <span className="text-white text-xs font-bold uppercase tracking-widest flex items-center">
                              View Details <ChevronRight className="h-4 w-4 ml-1" />
                            </span>
                          </div>
                        </div>
                        <h3 className="text-xl font-serif font-bold text-slate-900 group-hover:text-amber-700 transition-colors mb-2">
                          {parish.name}
                        </h3>
                        <div className="flex items-start text-slate-500 text-sm">
                          <MapPin className="h-4 w-4 mr-1.5 text-amber-500 shrink-0" />
                          <span className="line-clamp-1">{parish.location}</span>
                        </div>
                      </Link>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          );
        })}

        {filteredParishes.length === 0 && (
          <div className="text-center py-20 bg-white rounded-3xl border-2 border-dashed border-slate-100">
            <Church className="h-12 w-12 text-slate-200 mx-auto mb-4" />
            <p className="text-slate-400 italic font-serif">We couldn't find any parishes matching that search.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Parishes;